import { displayUnitLabel, unitConversionFactor } from './quantityUnits';
import { normalizeCanonicalFieldKey } from './fieldKeys';

export type SafetyMetric =
  | 'dose.single'
  | 'dose.daily_24h'
  | 'dose.loading'
  | 'dose.rate'
  | 'dose.interval'
  | 'administration.infusion_duration'
  | 'exposure.auc24'
  | 'exposure.cmin'
  | 'exposure.cmax';

export type SafetyRoute = 'iv_infusion' | 'iv_injection' | 'oral';

export type SafetyRuleSeverity = 'hard_block' | 'warning' | 'info';
export type SafetyRuleOperator = '<=' | '<' | '>=' | '>' | 'between';

export interface SafetyRule {
  id: string;
  name: string;
  description?: string;
  metric: SafetyMetric;
  operator: SafetyRuleOperator;
  value: number;
  upper_value?: number;
  unit: string;
  severity: SafetyRuleSeverity;
  enabled: boolean;
  methods?: string[];
  administration_modes?: string[];
  administration_routes?: SafetyRoute[];
  scope?: {
    methods?: string[];
    administration_modes?: string[];
    administration_routes?: SafetyRoute[];
    patient_conditions?: Record<string, unknown>;
  };
  patient_conditions?: Record<string, unknown>;
  source?: string;
}

type DrugSafetyConfig = {
  safety_rules?: unknown;
  safety_constraints?: Record<string, unknown>;
  continuous_infusion?: Record<string, unknown>;
  route?: unknown;
  routes?: unknown;
  [key: string]: unknown;
};

export const SAFETY_METRIC_OPTIONS: Array<{ value: SafetyMetric; label: string }> = [
  { value: 'dose.single', label: '单次剂量' },
  { value: 'dose.daily_24h', label: '24 小时总剂量' },
  { value: 'dose.loading', label: '负荷剂量' },
  { value: 'dose.rate', label: '给药速率' },
  { value: 'dose.interval', label: '给药间隔' },
  { value: 'administration.infusion_duration', label: '输注时长' },
  { value: 'exposure.auc24', label: '暴露 AUC24' },
  { value: 'exposure.cmin', label: '暴露 Cmin' },
  { value: 'exposure.cmax', label: '暴露 Cmax' },
];

export const SAFETY_OPERATOR_OPTIONS: Array<{ value: SafetyRuleOperator; label: string }> = [
  { value: '<=', label: '不高于（≤）' },
  { value: '<', label: '低于（<）' },
  { value: '>=', label: '不低于（≥）' },
  { value: '>', label: '高于（>）' },
  { value: 'between', label: '区间内' },
];

export const SAFETY_SEVERITY_OPTIONS: Array<{ value: SafetyRuleSeverity; label: string }> = [
  { value: 'hard_block', label: '硬性阻断' },
  { value: 'warning', label: '警告' },
  { value: 'info', label: '提示' },
];

export const SAFETY_ROUTE_OPTIONS: Array<{ value: SafetyRoute; label: string }> = [
  { value: 'iv_infusion', label: '静脉滴注（含连续输注）' },
  { value: 'iv_injection', label: '静脉注射' },
  { value: 'oral', label: '口服' },
];

const METRIC_LABELS = Object.fromEntries(
  SAFETY_METRIC_OPTIONS.map((option) => [option.value, option.label]),
) as Record<SafetyMetric, string>;

const UNIT_OPTIONS: Record<SafetyMetric, string[]> = {
  'dose.single': ['mg', 'g', 'mg/kg', 'g/kg', 'mg/m2', 'g/m2'],
  'dose.daily_24h': ['mg/day', 'g/day', 'mg/kg/day', 'g/kg/day', 'mg/m2/day', 'g/m2/day'],
  'dose.loading': ['mg', 'g', 'mg/kg', 'g/kg', 'mg/m2', 'g/m2'],
  'dose.rate': ['mg/h', 'g/h'],
  'dose.interval': ['h', 'min'],
  'administration.infusion_duration': ['min', 'h'],
  'exposure.auc24': ['mg*h/L', 'g*h/L', 'ug*h/mL', 'ug*h/L', 'ng*h/mL', 'ng*h/L'],
  'exposure.cmin': ['mg/L', 'g/L', 'ug/mL', 'ug/L', 'ng/mL', 'ng/L'],
  'exposure.cmax': ['mg/L', 'g/L', 'ug/mL', 'ug/L', 'ng/mL', 'ng/L'],
};

const DEFAULT_UNITS: Record<SafetyMetric, string> = {
  'dose.single': 'mg/kg',
  'dose.daily_24h': 'mg/kg/day',
  'dose.loading': 'mg/kg',
  'dose.rate': 'mg/h',
  'dose.interval': 'h',
  'administration.infusion_duration': 'min',
  'exposure.auc24': 'mg*h/L',
  'exposure.cmin': 'mg/L',
  'exposure.cmax': 'mg/L',
};

const METRICS = new Set<SafetyMetric>(SAFETY_METRIC_OPTIONS.map((option) => option.value));
const OPERATORS = new Set<SafetyRuleOperator>(SAFETY_OPERATOR_OPTIONS.map((option) => option.value));
const SEVERITIES = new Set<SafetyRuleSeverity>(SAFETY_SEVERITY_OPTIONS.map((option) => option.value));

const finiteNumber = (value: unknown): number | undefined => {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const stringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const normalized = value.map((item) => String(item || '').trim()).filter(Boolean);
  return normalized.length ? normalized : undefined;
};

const normalizeSafetyRoute = (value: unknown): SafetyRoute | undefined => {
  if (value && typeof value === 'object') {
    const item = value as Record<string, unknown>;
    value = item.code ?? item.label;
  }
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return undefined;
  if (['iv_infusion', 'iv infusion', 'intravenous infusion', 'infusion', 'iv', '静脉滴注', '静脉输注', '滴注', '输注'].includes(raw)) return 'iv_infusion';
  if (['iv_injection', 'iv injection', 'intravenous injection', 'bolus', '静脉注射', '静推'].includes(raw)) return 'iv_injection';
  if (['oral', 'po', 'oral administration', '口服'].includes(raw)) return 'oral';
  if (raw === 'intermittent' || raw === 'continuous') return 'iv_infusion';
  if (raw.includes('oral') || raw.includes('口服')) return 'oral';
  if (raw.includes('bolus') || raw.includes('静推') || raw.includes('静脉注射')) return 'iv_injection';
  if (raw.includes('infusion') || raw.includes('滴注') || raw.includes('输注')) return 'iv_infusion';
  return undefined;
};

const safetyRouteArray = (value: unknown): SafetyRoute[] | undefined => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  const routes = values.map(normalizeSafetyRoute).filter((route): route is SafetyRoute => Boolean(route));
  return routes.length ? Array.from(new Set(routes)) : undefined;
};

const makeRuleId = (): string => `safety-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getSafetyRuleUnitOptions = (metric: SafetyMetric): Array<{ value: string; label: string }> => (
  (UNIT_OPTIONS[metric] || [DEFAULT_UNITS[metric]]).map((unit) => ({
    value: unit,
    label: displayUnitLabel(unit),
  }))
);

export const createSafetyRule = (metric: SafetyMetric = 'dose.single'): SafetyRule => ({
  id: makeRuleId(),
  name: METRIC_LABELS[metric],
  metric,
  operator: metric === 'administration.infusion_duration' ? '>=' : '<=',
  value: 0,
  unit: DEFAULT_UNITS[metric],
  severity: metric.startsWith('exposure.') ? 'warning' : 'hard_block',
  enabled: true,
});

const normalizeRule = (raw: Record<string, unknown>, index: number): SafetyRule | null => {
  const metric = String(raw.metric || raw.metric_key || '').trim() as SafetyMetric;
  if (!METRICS.has(metric)) return null;
  const operator = String(raw.operator || '<=').trim().toLowerCase() as SafetyRuleOperator;
  if (!OPERATORS.has(operator)) return null;
  const value = finiteNumber(raw.value ?? raw.threshold ?? raw.threshold_value ?? raw.min);
  if (value === undefined) return null;
  const id = String(raw.id || raw.key || `safety-rule-${index + 1}`);
  const source = String(raw.source || '');
  if (value <= 0 && (id.startsWith('legacy.') || source.startsWith('legacy_'))) return null;
  const upperValue = finiteNumber(raw.upper_value ?? raw.max);
  if (operator === 'between' && upperValue === undefined) return null;
  const severity = String(raw.severity || 'warning').trim().toLowerCase() as SafetyRuleSeverity;
  const scope = raw.scope && typeof raw.scope === 'object'
    ? raw.scope as Record<string, unknown>
    : raw;
  const legacyRoutes = scope.routes;
  const legacyRouteValues = Array.isArray(legacyRoutes) ? legacyRoutes : legacyRoutes ? [legacyRoutes] : [];
  const legacyModeValues = new Set(['intermittent', 'continuous', 'oral', 'bolus']);
  const routeScope = scope.administration_routes
    ?? scope.route_codes
    ?? (legacyRouteValues.some((item) => !legacyModeValues.has(String(item).trim().toLowerCase())) ? legacyRoutes : undefined);
  const modeScope = scope.administration_modes
    ?? scope.calculation_modes
    ?? (legacyRouteValues.length > 0 && legacyRouteValues.every((item) => legacyModeValues.has(String(item).trim().toLowerCase()))
      ? legacyRoutes
      : undefined);
  return {
    ...raw,
    id,
    name: String(raw.name || raw.label || METRIC_LABELS[metric]),
    description: raw.description ? String(raw.description) : undefined,
    metric,
    operator,
    value,
    upper_value: upperValue,
    unit: String(raw.unit || DEFAULT_UNITS[metric]),
    severity: SEVERITIES.has(severity) ? severity : 'warning',
    enabled: raw.enabled !== false,
    methods: stringArray(scope.methods ?? scope.calculation_methods),
    administration_modes: stringArray(modeScope),
    administration_routes: safetyRouteArray(routeScope),
    patient_conditions: scope.patient_conditions && typeof scope.patient_conditions === 'object'
      ? scope.patient_conditions as Record<string, unknown>
      : undefined,
  };
};

const legacyRules = (config: DrugSafetyConfig): SafetyRule[] => {
  const constraints = config.safety_constraints && typeof config.safety_constraints === 'object'
    ? config.safety_constraints
    : {};
  const source: Record<string, unknown> = { ...config, ...constraints };
  const rules: SafetyRule[] = [];
  const add = (
    key: string,
    metric: SafetyMetric,
    unit: string,
    severity: SafetyRuleSeverity,
    operator: SafetyRuleOperator = '<=',
  ) => {
    const value = finiteNumber(source[key]);
    if (value === undefined || value <= 0) return;
    rules.push({
      id: `legacy.${key}`,
      name: METRIC_LABELS[metric],
      metric,
      operator,
      value,
      unit,
      severity,
      enabled: true,
      source: 'legacy_safety_constraints',
    });
  };
  add('max_dose_mg', 'dose.single', 'mg', 'hard_block');
  add('max_dose_mg_kg', 'dose.single', 'mg/kg', 'hard_block');
  add('max_dose_mg_m2', 'dose.single', 'mg/m2', 'hard_block');
  add('nephrotoxicity_auc24', 'exposure.auc24', 'mg*h/L', 'hard_block');
  add('auc24_max', 'exposure.auc24', 'mg*h/L', 'warning');
  add('nephrotoxicity_cmin', 'exposure.cmin', 'mg/L', 'warning');
  add('cmin_max', 'exposure.cmin', 'mg/L', 'warning');
  add('hepatotoxicity_cmin', 'exposure.cmin', 'mg/L', 'warning');
  add('nephrotoxicity_cmax', 'exposure.cmax', 'mg/L', 'warning');
  add('cmax_max', 'exposure.cmax', 'mg/L', 'warning');
  add('red_man_syndrome_infusion_min_minutes', 'administration.infusion_duration', 'min', 'hard_block', '>=');
  const continuous = config.continuous_infusion && typeof config.continuous_infusion === 'object'
    ? config.continuous_infusion as Record<string, unknown>
    : {};
  const addContinuousLoading = (key: string, unit: string) => {
    const value = finiteNumber(continuous[key]);
    if (value === undefined || value <= 0) return;
    rules.push({
      id: `legacy.continuous.${key}`,
      name: METRIC_LABELS['dose.loading'],
      metric: 'dose.loading',
      operator: '<=',
      value,
      unit,
      severity: 'hard_block',
      enabled: true,
      source: 'legacy_continuous_infusion',
    });
  };
  addContinuousLoading('max_loading_dose_mg', 'mg');
  addContinuousLoading('max_loading_dose_mg_kg', 'mg/kg');
  addContinuousLoading('max_loading_dose_mg_m2', 'mg/m2');
  return rules.filter((rule, index, all) => all.findIndex((candidate) => (
    candidate.metric === rule.metric
    && candidate.operator === rule.operator
    && candidate.value === rule.value
    && candidate.unit === rule.unit
    && candidate.severity === rule.severity
  )) === index);
};

export const normalizeSafetyRules = (config?: DrugSafetyConfig | null): SafetyRule[] => {
  if (!config) return [];
  if (!Array.isArray(config.safety_rules)) return legacyRules(config);
  const configuredRules = config.safety_rules
    .map((raw, index) => (
      raw && typeof raw === 'object' ? normalizeRule(raw as Record<string, unknown>, index) : null
    ))
    .filter((rule): rule is SafetyRule => rule !== null);
  const configuredLoading = configuredRules.filter((rule) => rule.metric === 'dose.loading');
  const legacyLoading = legacyRules(config).filter((rule) => rule.source === 'legacy_continuous_infusion');
  return [
    ...configuredRules,
    ...legacyLoading.filter((legacyRule) => !configuredLoading.some((rule) => (
      rule.operator === legacyRule.operator
      && rule.value === legacyRule.value
      && rule.unit === legacyRule.unit
    ))),
  ];
};

const convertThreshold = (value: number, fromUnit: string, toUnit: string): number | null => {
  if (fromUnit === toUnit) return value;
  try {
    return value * unitConversionFactor(fromUnit, toUnit);
  } catch {
    return null;
  }
};

const normalizeConditionValues = (values: Record<string, unknown> | undefined): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    const canonical = normalizeCanonicalFieldKey(key);
    if (!canonical) return;
    // Prefer a canonical, non-empty value over an alias or an empty value.
    if (!(canonical in normalized) || (normalized[canonical] == null || normalized[canonical] === '') && value !== '') {
      normalized[canonical] = value;
    }
  });
  return normalized;
};

const conditionValueEqual = (left: unknown, right: unknown): boolean => {
  if (typeof left === 'boolean' || typeof right === 'boolean') {
    const normalizeBoolean = (value: unknown) => {
      if (typeof value === 'boolean') return value;
      const token = String(value ?? '').trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on', '是', '有'].includes(token)) return true;
      if (['false', '0', 'no', 'n', 'off', '否', '无'].includes(token)) return false;
      return value;
    };
    return normalizeBoolean(left) === normalizeBoolean(right);
  }
  const leftNumber = finiteNumber(left);
  const rightNumber = finiteNumber(right);
  if (leftNumber !== undefined && rightNumber !== undefined) return leftNumber === rightNumber;
  return left === right || String(left ?? '').trim().toLowerCase() === String(right ?? '').trim().toLowerCase();
};

const compareConditionValue = (left: unknown, operator: unknown, right: unknown): boolean => {
  const op = String(operator || '==').trim().toLowerCase();
  const leftNumber = finiteNumber(left);
  const rightNumber = finiteNumber(right);
  const numeric = leftNumber !== undefined && rightNumber !== undefined;
  const a = numeric ? leftNumber as number : left;
  const b = numeric ? rightNumber as number : right;
  if (op === '=' || op === '==' || op === 'eq') return conditionValueEqual(a, b);
  if (op === '!=' || op === 'ne') return !conditionValueEqual(a, b);
  if (op === '<' || op === '<=' || op === '>' || op === '>=') {
    if (!numeric) return false;
    if (op === '<') return (a as number) < (b as number);
    if (op === '<=') return (a as number) <= (b as number);
    if (op === '>') return (a as number) > (b as number);
    return (a as number) >= (b as number);
  }
  if (op === 'in' || op === 'not_in' || op === 'not in') {
    const values = Array.isArray(right) ? right : [right];
    const matched = values.some((item) => conditionValueEqual(left, item));
    return op === 'in' ? matched : !matched;
  }
  if (op === 'between') {
    const values = Array.isArray(right) ? right : [];
    const lower = finiteNumber(values[0]);
    const upper = finiteNumber(values[1]);
    return numeric && lower !== undefined && upper !== undefined
      ? (a as number) >= lower && (a as number) <= upper
      : false;
  }
  return false;
};

/** Evaluate the same patient-condition grammar used by backend regimen/safety rules. */
export const safetyConditionsMatch = (
  condition: unknown,
  values: Record<string, unknown> | undefined,
): boolean => {
  const normalized = normalizeConditionValues(values);
  const evaluate = (node: unknown): boolean => {
    if (!node || typeof node !== 'object' || !Object.keys(node as Record<string, unknown>).length) return true;
    const expression = node as Record<string, unknown>;
    if ('all' in expression) return (Array.isArray(expression.all) ? expression.all : []).every(evaluate);
    if ('any' in expression) return (Array.isArray(expression.any) ? expression.any : []).some(evaluate);
    if ('not' in expression) return !evaluate(expression.not);
    const field = normalizeCanonicalFieldKey(String(expression.field || ''));
    if (!field || normalized[field] == null || normalized[field] === '') return false;
    return compareConditionValue(
      normalized[field],
      expression.op ?? expression.operator,
      expression.value,
    );
  };
  return evaluate(condition);
};

export const getExposureSafetyLimit = (
  config: DrugSafetyConfig | null | undefined,
  metric: Extract<SafetyMetric, 'exposure.auc24' | 'exposure.cmin' | 'exposure.cmax'>,
  unit: string,
  context: {
    selectedMethod?: string;
    administrationMode?: string;
    administrationRoute?: SafetyRoute;
    covariates?: Record<string, unknown>;
  } = {},
): number | undefined => {
  const resolvedRoute = context.administrationRoute
    ?? normalizeSafetyRoute(context.administrationMode);
  const limits = normalizeSafetyRules(config)
    .filter((rule) => (
      rule.enabled
      && rule.metric === metric
      && ['<=', '<'].includes(rule.operator)
      && ['hard_block', 'warning'].includes(rule.severity)
      && (!rule.methods?.length || Boolean(context.selectedMethod && rule.methods.includes(context.selectedMethod)))
      && (!rule.administration_modes?.length || Boolean(context.administrationMode && rule.administration_modes.includes(context.administrationMode)))
      && (!rule.administration_routes?.length || Boolean(resolvedRoute && rule.administration_routes.includes(resolvedRoute)))
      && safetyConditionsMatch(rule.patient_conditions, context.covariates)
    ))
    .map((rule) => convertThreshold(rule.value, rule.unit, unit))
    .filter((value): value is number => value !== null);
  return limits.length ? Math.min(...limits) : undefined;
};

const strictUpperLimit = (
  rules: SafetyRule[],
  metric: SafetyMetric,
  unit: string,
  severities: SafetyRuleSeverity[] = ['hard_block', 'warning'],
): number | null => {
  const values = rules
    .filter((rule) => (
      rule.enabled
      && rule.metric === metric
      && ['<=', '<'].includes(rule.operator)
      && severities.includes(rule.severity)
      // The legacy flat fields cannot represent method, mode, or route scope.
      // Do not mirror scoped rules into them, otherwise an IV limit can become
      // an unintended global limit for oral or bolus calculations.
      && !rule.methods?.length
      && !rule.administration_modes?.length
      && !rule.administration_routes?.length
      && !rule.patient_conditions
    ))
    .map((rule) => convertThreshold(rule.value, rule.unit, unit))
    .filter((value): value is number => value !== null);
  return values.length ? Math.min(...values) : null;
};

export const buildLegacySafetyConstraints = (
  rules: SafetyRule[],
  existing?: Record<string, unknown>,
): Record<string, unknown> => {
  const next = { ...(existing || {}) };
  const assign = (key: string, metric: SafetyMetric, unit: string) => {
    next[key] = strictUpperLimit(rules, metric, unit);
  };
  const assignHardBlock = (key: string, metric: SafetyMetric, unit: string) => {
    next[key] = strictUpperLimit(rules, metric, unit, ['hard_block']);
  };
  assignHardBlock('max_dose_mg', 'dose.single', 'mg');
  assignHardBlock('max_dose_mg_kg', 'dose.single', 'mg/kg');
  assignHardBlock('max_dose_mg_m2', 'dose.single', 'mg/m2');
  assign('nephrotoxicity_auc24', 'exposure.auc24', 'mg*h/L');
  assign('nephrotoxicity_cmin', 'exposure.cmin', 'mg/L');
  assign('nephrotoxicity_cmax', 'exposure.cmax', 'mg/L');
  const infusionMinimums = rules
    .filter((rule) => rule.enabled && rule.metric === 'administration.infusion_duration' && ['>=', '>'].includes(rule.operator))
    .map((rule) => convertThreshold(rule.value, rule.unit, 'min'))
    .filter((value): value is number => value !== null);
  next.red_man_syndrome_infusion_min_minutes = infusionMinimums.length
    ? Math.max(...infusionMinimums)
    : null;
  return next;
};

export const serializeSafetyRule = (rule: SafetyRule): Record<string, unknown> => ({
  id: rule.id || makeRuleId(),
  name: String(rule.name || METRIC_LABELS[rule.metric]).trim(),
  description: String(rule.description || '').trim(),
  metric: rule.metric,
  operator: rule.operator,
  value: Number(rule.value),
  upper_value: rule.operator === 'between' ? Number(rule.upper_value) : undefined,
  unit: rule.unit,
  severity: rule.severity,
  enabled: rule.enabled !== false,
  scope: {
    methods: stringArray(rule.methods) || [],
    administration_modes: stringArray(rule.administration_modes) || [],
    administration_routes: safetyRouteArray(rule.administration_routes) || [],
    ...(rule.patient_conditions && Object.keys(rule.patient_conditions).length
      ? { patient_conditions: rule.patient_conditions }
      : {}),
  },
});

export const formatSafetyRuleSummary = (rule: SafetyRule): string => {
  const operator = rule.operator === 'between'
    ? `${rule.value} - ${rule.upper_value}`
    : `${rule.operator} ${rule.value}`;
  return `${rule.name || METRIC_LABELS[rule.metric]}：${operator} ${displayUnitLabel(rule.unit)}`;
};
